import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

interface Option {
  label: string;
  value: string | number;
}

interface CustomSelectProps {
  options: Option[];
  selectedValue: string | number | null;
  onValueChange: (value: string | number) => void;
  placeholder?: string;
  theme: any;
  enabled?: boolean;
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
}

export const CustomSelect = ({
  options,
  selectedValue,
  onValueChange,
  placeholder = 'Selecciona una opción...',
  theme,
  enabled = true,
  style,
  textStyle,
}: CustomSelectProps) => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const selectedOption = options.find(o => o.value === selectedValue);

  // Abrir / cerrar el <dialog> nativo
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();
      if (
        e.clientX < rect.left || e.clientX > rect.right ||
        e.clientY < rect.top  || e.clientY > rect.bottom
      ) {
        setOpen(false);
      }
    };

    if (open) {
      dialog.showModal();
      dialog.addEventListener('click', handleNativeClick);
    } else {
      dialog.close();
      dialog.removeEventListener('click', handleNativeClick);
    }

    return () => {
      dialog.removeEventListener('click', handleNativeClick);
    };
  }, [open]);

  const handleSelect = (val: string | number) => {
    onValueChange(val);
    setOpen(false);
  };

  return (
    <>
      {/* Selector */}
      <button
        type="button"
        onClick={() => enabled && setOpen(true)}
        disabled={!enabled}
        className={`flex items-center justify-between w-full h-14 px-4 rounded-lg border bg-transparent transition-opacity ${enabled ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-50'}`}
        style={{
          borderColor: theme.background || '#E2E8F0',
          ...style,
        }}
      >
        <span 
          className="text-base truncate"
          style={{ color: selectedOption ? theme.text : theme.textSecondary, ...textStyle }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={20} color={theme.textSecondary} className="shrink-0 ml-2" />
      </button>

      {/* Modal nativo <dialog> */}
      <dialog
        ref={dialogRef}
        className="p-0 border-none rounded-xl w-[90%] max-w-lg max-h-[80vh] overflow-hidden shadow-2xl backdrop:bg-black/60"
        style={{
          backgroundColor: theme.card || '#FFFFFF',
        }}
      >
        {/* Header */}
        <div 
          className="flex justify-between items-center px-4 h-14 border-b"
          style={{ borderColor: theme.background || '#E2E8F0' }}
        >
          <span className="text-lg font-bold truncate" style={{ color: theme.text }}>
            {placeholder}
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1 cursor-pointer bg-transparent border-none"
          >
            <X size={24} color={theme.textSecondary} />
          </button>
        </div>

        {/* Lista de opciones */}
        <ul className="list-none m-0 p-0 overflow-y-auto max-h-[60vh]">
          {options.map(item => {
            const isSelected = item.value === selectedValue;
            return (
              <li key={String(item.value)}>
                <button
                  type="button"
                  onClick={() => handleSelect(item.value)}
                  className="flex items-center justify-between w-full px-4 h-14 border-none border-b cursor-pointer text-left transition-colors"
                  style={{
                    borderColor: theme.background || '#E2E8F0',
                    backgroundColor: isSelected ? (theme.background || '#F8FAFC') : 'transparent',
                  }}
                >
                  <span 
                    className={`text-base truncate ${isSelected ? 'font-bold' : 'font-normal'}`}
                    style={{
                      color: isSelected ? (theme.primary || '#2563EB') : theme.text,
                    }}
                  >
                    {item.label}
                  </span>
                  {isSelected && (
                    <Check size={20} color={theme.primary || '#2563EB'} className="shrink-0 ml-2" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </dialog>
    </>
  );
};
